import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  activateMiniGamePlayer,
  finishGameSession,
  deactivateMiniGamePlayer,
  GAME_LAUNCH_EVENT,
  GAME_MINI_PLAYER_EVENT,
  GAME_MINI_PLAYER_DEFAULT_SIZE,
  GAME_PLAYER_PATH,
  readStoredGameLaunch,
  readStoredMiniGamePlayer,
  GAME_PLAYER_FRAME_LOADED_EVENT,
} from "../../../lib/gameLaunch";
import { StyledGameMiniPlayer } from "./StyledGameMiniPlayer";
import { isGameFrameLoaded } from '../../../features/gamePlayer/state/frameLoad.state';

const getLaunchTitle = (launch) => launch?.label || "게임";
const MINI_PLAYER_MIN_SIZE = { width: 460, height: 300 };
const MINI_PLAYER_MAX_SIZE = { width: 860, height: 560 };
const MINI_PLAYER_STEP = { width: 120, height: 78 };

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const GameMiniPlayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [miniState, setMiniState] = useState(() => readStoredMiniGamePlayer());
  const [launch, setLaunch] = useState(() => readStoredGameLaunch());
  const [isLoading, setIsLoading] = useState(() => !isGameFrameLoaded(readStoredGameLaunch()));

  const shouldRender = Boolean(miniState?.active && launch?.playUrl);
  const title = getLaunchTitle(launch);
  const defaultSize = useMemo(
    () => ({
      width: miniState?.width || GAME_MINI_PLAYER_DEFAULT_SIZE.width,
      height: miniState?.height || GAME_MINI_PLAYER_DEFAULT_SIZE.height,
    }),
    [miniState?.height, miniState?.width]
  );

  useEffect(() => {
    const handleLoaded = () => setIsLoading(false);
    window.addEventListener(GAME_PLAYER_FRAME_LOADED_EVENT, handleLoaded);
    return () => window.removeEventListener(GAME_PLAYER_FRAME_LOADED_EVENT, handleLoaded);
  }, []);

  useEffect(() => {
    const syncState = () => {
      setMiniState(readStoredMiniGamePlayer());
      setLaunch(readStoredGameLaunch());
      setIsLoading(!isGameFrameLoaded(readStoredGameLaunch()));
    };

    window.addEventListener(GAME_MINI_PLAYER_EVENT, syncState);
    window.addEventListener(GAME_LAUNCH_EVENT, syncState);
    return () => {
      window.removeEventListener(GAME_MINI_PLAYER_EVENT, syncState);
      window.removeEventListener(GAME_LAUNCH_EVENT, syncState);
    };
  }, []);

  const handleClose = () => {
    finishGameSession("mini-player-close");
    setMiniState(null);
    setLaunch(null);
  };

  const handleExpand = () => {
    deactivateMiniGamePlayer();
    setMiniState(null);
    navigate(GAME_PLAYER_PATH);
  };

  const handleResizeMini = (direction) => {
    const multiplier = direction === "larger" ? 1 : -1;
    const nextSize = {
      width: clamp(
        defaultSize.width + MINI_PLAYER_STEP.width * multiplier,
        MINI_PLAYER_MIN_SIZE.width,
        MINI_PLAYER_MAX_SIZE.width
      ),
      height: clamp(
        defaultSize.height + MINI_PLAYER_STEP.height * multiplier,
        MINI_PLAYER_MIN_SIZE.height,
        MINI_PLAYER_MAX_SIZE.height
      ),
    };

    activateMiniGamePlayer(nextSize);
    setMiniState(readStoredMiniGamePlayer());
  };

  if (!shouldRender || location.pathname === GAME_PLAYER_PATH) return null;

  return (
    <StyledGameMiniPlayer
      aria-label={`${title} 미니플레이어`}
      role="complementary"
      style={{
        width: `${defaultSize.width}px`,
        height: `${defaultSize.height}px`,
      }}
    >
      <div className="mini-player-topbar">
        <div className="mini-player-title">
          <span>{launch.vendorName || "게임"}</span>
          <strong>{title}</strong>
        </div>
        <div className="mini-player-actions">
          <button type="button" onClick={() => handleResizeMini("smaller")}>
            작게
          </button>
          <button type="button" onClick={() => handleResizeMini("larger")}>
            크게
          </button>
          <button type="button" onClick={handleExpand}>
            영화관
          </button>
          <button aria-label="미니플레이어 닫기" type="button" onClick={handleClose}>
            ×
          </button>
        </div>
      </div>
      <div className="mini-player-frame-wrap">
        <div data-game-frame-slot="mini" className="mini-player-frame" />
        <div className={`mini-player-loading ${isLoading ? "" : "is-hidden"}`}>로딩 중</div>
      </div>
    </StyledGameMiniPlayer>
  );
};

export default GameMiniPlayer;
